import { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Simple in-memory cache shared across components.
// Key: `${userId}:${submissionId}` -> boolean (true if liked)
const likeCache = new Map();
// Track in-flight loads so we don't duplicate getDoc calls
const loadingKeys = new Set();

export function useLikeStateCache(userId) {
  const [likedIds, setLikedIds] = useState(new Set());
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // When the userId changes, derive the current liked set from the global cache
  useEffect(() => {
    if (!userId) {
      setLikedIds(new Set());
      return;
    }

    const next = new Set();
    for (const [key, value] of likeCache.entries()) {
      if (!value) continue;
      const [uid, submissionId] = key.split(':');
      if (uid === userId) {
        next.add(submissionId);
      }
    }
    setLikedIds(next);
  }, [userId]);

  const ensureLoaded = useCallback(
    async (submissionId) => {
      if (!userId || !submissionId) return;
      const cacheKey = `${userId}:${submissionId}`;

      // If we already know the value, just mirror it into local state if liked
      if (likeCache.has(cacheKey)) {
        if (likeCache.get(cacheKey)) {
          setLikedIds((prev) => {
            if (prev.has(submissionId)) return prev;
            const next = new Set(prev);
            next.add(submissionId);
            return next;
          });
        }
        return;
      }

      // Avoid duplicate concurrent loads
      if (loadingKeys.has(cacheKey)) return;
      loadingKeys.add(cacheKey);

      try {
        const likeDoc = await getDoc(
          doc(db, 'submissions', submissionId, 'likes', userId)
        );
        const isLiked = likeDoc.exists();
        likeCache.set(cacheKey, isLiked);

        if (!isMountedRef.current) return;
        if (isLiked) {
          setLikedIds((prev) => {
            if (prev.has(submissionId)) return prev;
            const next = new Set(prev);
            next.add(submissionId);
            return next;
          });
        }
      } catch (error) {
        // Swallow errors here; caller can log if needed
        // We simply don't mark as liked in case of failure.
      } finally {
        loadingKeys.delete(cacheKey);
      }
    },
    [userId]
  );

  const setLikedOptimistic = useCallback(
    (submissionId, isLiked) => {
      if (!userId || !submissionId) return;
      const cacheKey = `${userId}:${submissionId}`;
      likeCache.set(cacheKey, isLiked);

      setLikedIds((prev) => {
        const next = new Set(prev);
        if (isLiked) {
          next.add(submissionId);
        } else {
          next.delete(submissionId);
        }
        return next;
      });
    },
    [userId]
  );

  const isLiked = useCallback(
    (submissionId) => likedIds.has(submissionId),
    [likedIds]
  );

  return {
    isLiked,
    ensureLoaded,
    setLikedOptimistic,
    likedIds,
  };
}

